/**
 * NativeInvoiceDraft — generates a client invoice from CaseMinder's native
 * invoice engine (time-recording-foundations, invoice engine integration)
 * instead of May keying it into LawBillity by hand.
 *
 * Deliberately does NOT duplicate InvoiceRequestDetail.jsx's existing
 * draft-upload / notify / approve / scratchpad / send / file-to-SharePoint
 * pipeline (Vanessa, 26 Sep 2026: "there is a working invoice request page
 * which works well, use it, don't build anything you don't need to
 * build"). This component only produces a PDF and hands it to the
 * caller — the caller feeds it into the SAME draft_file_data /
 * final_invoice_data tracker fields a manual upload already sets, so every
 * downstream step keeps working unchanged.
 *
 * Two stages, one component:
 *   stage="draft" — create/edit the draft, "Use as Draft" renders the PDF
 *                   and hands it back. Never issues a real invoice number.
 *   stage="issue" — same editor, but the action issues the invoice for
 *                   real (stamps invoice_number, marks linked time
 *                   entries/disbursements billed) before handing back the
 *                   PDF. Irreversible — only shown once already Approved.
 *
 * BM-H4539 (20 Oct 2026): "Total due" used to show the last SAVED total, so
 * typing an amount changed nothing on screen, and "Use as Draft" / "Issue"
 * rendered the saved invoice, silently dropping unsaved edits. May read a
 * total of £30 off a draft whose lines said otherwise. The total now follows
 * the lines as they are typed (same maths as hub.invoices._compute_totals,
 * VAT rate read from the server's totals), and both actions save pending
 * edits first. Save also keeps each line's link to its time entry or expense
 * (it used to drop it, so issuing never marked those billed).
 *
 * BM-H4554 (20 Oct 2026, May Ann): "Use as Draft" is now Save draft, and
 * Update draft once the request has one; it no longer emails the fee
 * earner (May sends it with Send to fee earner when it is ready). "Issue &
 * Use as Final Invoice" is Finalise invoice, which also opens the credit
 * control record on the server.
 *
 * BM-H4632 (23 Oct 2026): each Professional Services line carries its
 * billing type (Fixed fee / Hourly), printed in the invoice's Basis column
 * as LawBillity does. Lines can be dragged into order by their grip (or
 * moved with the arrow keys on it) before finalising; the saved order is
 * the printed order.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, Trash2, Plus, AlertTriangle, FileCheck, Receipt, GripVertical, Clock, CalendarArrowUp, Ban, Percent, PoundSterling } from "lucide-react";
import { Button } from "./ui/button";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

function fmtGbp(amount) {
  const n = Number(amount);
  return Number.isFinite(n) ? `£${n.toFixed(2)}` : "£0.00";
}

// Mirrors hub.invoices._compute_totals: round every money figure UP to the
// penny, VAT on professional services and on "+ VAT" lines only. Works in
// pence to keep float error out. Returns null when the rate is unknown, so
// the screen falls back to the saved total rather than guessing.
const toPence = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.ceil(Math.round(n * 1e6) / 1e4) : 0;
};
const vatPence = (netPence, rate) => Math.ceil(Math.round(netPence * rate * 1e4) / 1e4);

// Mirrors hub.invoices.discount_amount: rounded up, never more than the fees.
export function discountPence(discount, feesPence) {
  const value = Number(discount?.value);
  if (!discount || !Number.isFinite(value) || value <= 0 || feesPence <= 0) return 0;
  const raw = discount.kind === "percent" ? Math.ceil(Math.round(feesPence * value * 1e4) / 1e6) : toPence(value);
  return Math.min(raw, feesPence);
}

export function liveTotalDue(invoice) {
  const rate = Number(invoice?.totals?.vat_rate);
  if (!invoice || !Number.isFinite(rate)) return null;
  const sum = (lines, pred = () => true) => (lines || []).filter(pred).reduce((a, l) => a + toPence(l.amount), 0);
  // BM-H5030: written-off time isn't charged; the discount comes off before VAT.
  const fees = sum(invoice.professional_services, (l) => !l.written_off);
  const prof = fees - discountPence(invoice.discount, fees);
  const plusVat = (l) => l.vat_treatment === "plus_vat";
  const disbPlus = sum(invoice.disbursement_lines, plusVat);
  const otherPlus = sum(invoice.other_fee_lines, plusVat);
  const total = prof + (prof > 0 ? vatPence(prof, rate) : 0)
    + sum(invoice.disbursement_lines) + (disbPlus > 0 ? vatPence(disbPlus, rate) : 0)
    + sum(invoice.other_fee_lines) + (otherPlus > 0 ? vatPence(otherPlus, rate) : 0);
  return (total / 100).toFixed(2);
}

// BM-H4632: billing_type and the time entry's hours/rate/date ride along
// with the links, so a save never drops what the invoice prints.
const LINK_KEYS = ["time_entry_id", "disbursement_id", "billing_type", "hours", "rate", "entry_date", "fee_earner",
  "written_off", "write_off_reason"]; // BM-H5030

// BM-H4873: "<matter ref> Invoice <number>.pdf", DRAFT before issue (matches
// the backend's invoice_file_name).
export const invoiceFileName = (matterRef, invoiceNumber) =>
  `${matterRef || ""} Invoice ${invoiceNumber || "DRAFT"}.pdf`.trim();
const keepLinks = (l) => Object.fromEntries(LINK_KEYS.filter((k) => l[k]).map((k) => [k, l[k]]));

export function moveItem(list, from, to) {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

// BM-H5019 (May, 3 Nov 2026): time lines oldest first. Lines with no date
// (a fixed fee, a line typed in) stay at the top in the order they were in.
export function sortByDate(lines) {
  const undated = lines.filter((l) => !l.entry_date);
  const dated = lines.filter((l) => l.entry_date)
    .map((l, i) => [l, i])
    .sort(([a, i], [b, j]) => (a.entry_date < b.entry_date ? -1 : a.entry_date > b.entry_date ? 1 : i - j))
    .map(([l]) => l);
  return [...undated, ...dated];
}

function LineRow({ line, onChange, onRemove, showVat, showBilling, showWriteOff, drag }) {
  const off = !!line.written_off;
  return (
    <div
      className={`py-1.5 ${drag.over ? "border-t-2 border-teal" : ""}`}
      data-testid="native-invoice-line-row"
      onDragOver={(e) => { e.preventDefault(); drag.onOver(); }}
      onDrop={(e) => { e.preventDefault(); drag.onDrop(); }}
    >
    <div className={`flex items-start gap-2 ${off ? "opacity-60" : ""}`}>
      <button
        type="button"
        draggable
        onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; drag.onStart(); }}
        onDragEnd={drag.onEnd}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp") { e.preventDefault(); drag.onStep(-1); }
          if (e.key === "ArrowDown") { e.preventDefault(); drag.onStep(1); }
        }}
        className="p-1 text-slate-500 hover:text-slate-300 cursor-grab"
        title="Drag to reorder (or arrow keys)"
        aria-label="Reorder line"
        data-testid="native-invoice-line-grip"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <input
        type="text"
        value={line.description}
        onChange={(e) => onChange({ ...line, description: e.target.value })}
        placeholder="Description"
        className="flex-1 p-1.5 rounded border border-white/10 text-xs"
      />
      {showBilling && (
        <select
          value={line.billing_type || ""}
          onChange={(e) => onChange({ ...line, billing_type: e.target.value || undefined })}
          className="p-1.5 rounded border border-white/10 text-xs"
          title="Printed on the invoice"
          data-testid="native-invoice-line-billing"
        >
          <option value="">Basis…</option>
          <option value="fixed">Fixed fee</option>
          <option value="hourly">Hourly</option>
        </select>
      )}
      {showVat && (
        <select
          value={line.vat_treatment || "no_vat"}
          onChange={(e) => onChange({ ...line, vat_treatment: e.target.value })}
          className="p-1.5 rounded border border-white/10 text-xs"
        >
          <option value="no_vat">No VAT</option>
          <option value="plus_vat">+ VAT</option>
          <option value="inc_vat">Inc VAT</option>
        </select>
      )}
      <input
        type="number"
        step="0.01"
        value={line.amount}
        onChange={(e) => onChange({ ...line, amount: e.target.value })}
        placeholder="0.00"
        className={`w-24 p-1.5 rounded border border-white/10 text-xs text-right ${off ? "line-through" : ""}`}
      />
      {showWriteOff && (
        <button
          type="button"
          onClick={() => onChange(off ? { ...line, written_off: undefined, write_off_reason: undefined } : { ...line, written_off: true })}
          className={`p-1 ${off ? "text-status-amber" : "text-slate-400 hover:text-status-amber"}`}
          title={off ? "Charge this time again" : "Write off (not charged)"}
          aria-label={off ? "Undo write-off" : "Write off"}
          aria-pressed={off}
          data-testid="native-invoice-line-write-off"
        >
          <Ban className="w-3.5 h-3.5" />
        </button>
      )}
      <button type="button" onClick={onRemove} className="text-slate-400 hover:text-status-red p-1" data-testid="native-invoice-line-remove">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
    {off && (
      <input
        type="text"
        value={line.write_off_reason || ""}
        onChange={(e) => onChange({ ...line, write_off_reason: e.target.value })}
        placeholder="Reason for writing off (printed on the invoice)"
        aria-label="Write-off reason"
        className={`mt-1 ml-7 w-[calc(100%-1.75rem)] p-1.5 rounded border text-xs ${line.write_off_reason?.trim() ? "border-white/10" : "border-status-amber"}`}
        data-testid="native-invoice-line-write-off-reason"
      />
    )}
    </div>
  );
}

export default function NativeInvoiceDraft({
  submissionId,
  stage, // "draft" | "issue"
  existingInvoiceId, // set once the draft stage has already created one — reuse it, never regenerate
  onReady, // ({ pdfBase64, filename, invoiceNumber, invoiceId, totalDue }) => void
}) {
  // BM-H4554: an existing draft opens straight into the editor at either
  // stage, so Update edits the request's own draft in place.
  const startsPreloaded = !!existingInvoiceId;
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(startsPreloaded);
  const [saving, setSaving] = useState(false);
  const [working, setWorking] = useState(false);
  const [started, setStarted] = useState(startsPreloaded);
  const [dirty, setDirty] = useState(false);

  const currentUserEmail = sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "";

  // The "issue" stage always has an existing invoice from the draft
  // stage (that's what got attached as draft_file_data) — fetch it
  // rather than calling from-request again, which would silently spin
  // up a SECOND client_invoices record and throw away any edits made
  // during draft review.
  const handleFetchExisting = useCallback(async () => {
    setLoading(true);
    setStarted(true);
    try {
      const resp = await axios.get(`${API}/invoices/${existingInvoiceId}`, {
        headers: { "X-User-Email": currentUserEmail },
      });
      setInvoice(resp.data);
    } catch (err) {
      toast.error(errorMessage(err, "Could not load the invoice draft"));
      setStarted(false);
    } finally {
      setLoading(false);
    }
  }, [existingInvoiceId, currentUserEmail]);

  const handleGenerate = useCallback(async () => {
    if (existingInvoiceId) return handleFetchExisting();
    setLoading(true);
    setStarted(true);
    try {
      const resp = await axios.post(`${API}/invoices/from-request/${submissionId}`, {}, {
        headers: { "X-User-Email": currentUserEmail },
      });
      setInvoice(resp.data);
    } catch (err) {
      toast.error(errorMessage(err, "Could not generate invoice"));
      setStarted(false);
    } finally {
      setLoading(false);
    }
  }, [submissionId, currentUserEmail, existingInvoiceId, handleFetchExisting]);

  // At the "issue" stage the invoice already exists — load it immediately
  // rather than waiting for a "Generate Invoice" click (that button's copy
  // doesn't fit here; the review editor should just appear). `started` is
  // already true for this case (see startsPreloaded above) purely to skip
  // the button flash, so it can't also gate this effect — a ref tracks
  // whether the fetch itself has actually fired, and survives a
  // dev-mode double-invoke without double-fetching.
  const preloadFetchedRef = useRef(false);
  useEffect(() => {
    if (existingInvoiceId && !preloadFetchedRef.current) {
      preloadFetchedRef.current = true;
      handleFetchExisting();
    }
  }, [existingInvoiceId, handleFetchExisting]);

  // BM-H4841: a request's draft is reused, so time recorded after it was
  // made (May's Ivata LawBillity import, 28 Oct 2026) is offered here rather
  // than silently left off. Lines already on this draft are not offered twice.
  const [newTime, setNewTime] = useState([]);
  const invoiceId = invoice?.id;
  const invoiceStatus = invoice?.status;
  useEffect(() => {
    if (!invoiceId || invoiceStatus !== "draft") return;
    let live = true;
    axios.get(`${API}/invoices/${invoiceId}/unbilled-time`, { headers: { "X-User-Email": currentUserEmail } })
      .then((resp) => { if (live) setNewTime(resp.data?.lines || []); })
      .catch(() => { if (live) setNewTime([]); });
    return () => { live = false; };
  }, [invoiceId, invoiceStatus, currentUserEmail]);
  const onDraft = new Set((invoice?.professional_services || []).map((l) => l.time_entry_id).filter(Boolean));
  const offeredTime = newTime.filter((l) => !onDraft.has(l.time_entry_id));
  const offeredTotal = offeredTime.reduce((a, l) => a + Number(l.amount || 0), 0);
  const handleAddNewTime = () => {
    setDirty(true);
    setInvoice((prev) => ({ ...prev, professional_services: sortByDate([...prev.professional_services, ...offeredTime]) }));
  };
  const handleSortByDate = () => {
    setInvoice((prev) => {
      const sorted = sortByDate(prev.professional_services || []);
      if (sorted.every((l, i) => l === prev.professional_services[i])) return prev;
      setDirty(true);
      return { ...prev, professional_services: sorted };
    });
  };
  const handleDiscount = (patch) => {
    setDirty(true);
    setInvoice((prev) => ({ ...prev, discount: { kind: "amount", value: "", reason: "", ...(prev.discount || {}), ...patch } }));
  };
  const handleInvoiceTo = (key, value) => {
    setDirty(true);
    setInvoice((prev) => ({ ...prev, [key]: value }));
  };

  const handleLineChange = (section, idx, next) => {
    setDirty(true);
    setInvoice((prev) => {
      const lines = [...prev[section]];
      lines[idx] = next;
      return { ...prev, [section]: lines };
    });
  };

  const handleAddLine = (section) => {
    setDirty(true);
    setInvoice((prev) => ({
      ...prev,
      [section]: [...(prev[section] || []), {
        description: "", amount: "0.00", _k: `new-${Date.now()}-${Math.random()}`,
        ...(section === "disbursement_lines" || section === "other_fee_lines" ? { vat_treatment: "no_vat" } : {}),
        ...(section === "professional_services" ? { billing_type: prev.fee_basis === "fixed" ? "fixed" : "hourly" } : {}),
      }],
    }));
  };

  // BM-H4632: drag (or arrow keys on the grip) to reorder within a section.
  const [dragging, setDragging] = useState(null); // { section, idx }
  const [dragOver, setDragOver] = useState(null);
  const handleMoveLine = (section, from, to) => {
    setInvoice((prev) => {
      const lines = prev[section] || [];
      const moved = moveItem(lines, from, to);
      if (moved === lines) return prev;
      setDirty(true);
      return { ...prev, [section]: moved };
    });
  };
  const dragProps = (section, idx) => ({
    over: dragging?.section === section && dragOver === idx && dragging.idx !== idx,
    onStart: () => setDragging({ section, idx }),
    onOver: () => { if (dragging?.section === section) setDragOver(idx); },
    onDrop: () => {
      if (dragging?.section === section) handleMoveLine(section, dragging.idx, idx);
      setDragging(null); setDragOver(null);
    },
    onEnd: () => { setDragging(null); setDragOver(null); },
    onStep: (delta) => handleMoveLine(section, idx, idx + delta),
  });

  const handleRemoveLine = (section, idx) => {
    setDirty(true);
    setInvoice((prev) => ({ ...prev, [section]: prev[section].filter((_, i) => i !== idx) }));
  };

  // Returns the saved invoice, or null when the save failed (already toasted).
  const saveInvoice = useCallback(async ({ quiet = false } = {}) => {
    setSaving(true);
    try {
      const resp = await axios.patch(`${API}/invoices/${invoice.id}`, {
        ...(invoice.client_display_name?.trim() ? { client_display_name: invoice.client_display_name } : {}),
        invoice_to_address: invoice.invoice_to_address || "",
        discount: Number(invoice.discount?.value) > 0 ? invoice.discount : null,
        professional_services: invoice.professional_services.map((l) => ({
          description: l.description, amount: String(l.amount), ...keepLinks(l),
        })),
        disbursement_lines: invoice.disbursement_lines.map((l) => ({
          description: l.description, amount: String(l.amount), vat_treatment: l.vat_treatment || "no_vat", ...keepLinks(l),
        })),
        other_fee_lines: (invoice.other_fee_lines || []).map((l) => ({
          description: l.description, amount: String(l.amount), vat_treatment: l.vat_treatment || "no_vat",
        })),
      }, { headers: { "X-User-Email": currentUserEmail } });
      setInvoice(resp.data);
      setDirty(false);
      if (!quiet) toast.success("Invoice draft saved");
      return resp.data;
    } catch (err) {
      toast.error(errorMessage(err, "Could not save invoice draft"));
      return null;
    } finally {
      setSaving(false);
    }
  }, [invoice, currentUserEmail]);

  const handleSave = useCallback(() => saveInvoice(), [saveInvoice]);

  // BM-H4539: never render a PDF from a stale saved copy.
  const savedInvoice = useCallback(async () => (dirty ? saveInvoice({ quiet: true }) : invoice), [dirty, saveInvoice, invoice]);

  const fetchPdfAsBase64 = useCallback(async (invoiceId) => {
    const resp = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
      headers: { "X-User-Email": currentUserEmail },
      responseType: "arraybuffer",
    });
    const bytes = new Uint8Array(resp.data);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }, [currentUserEmail]);

  const handleUseAsDraft = useCallback(async () => {
    const current = await savedInvoice();
    if (!current) return;
    setWorking(true);
    try {
      const pdfBase64 = await fetchPdfAsBase64(current.id);
      const filename = invoiceFileName(current.matter_ref);
      onReady({ pdfBase64, filename, invoiceNumber: null, invoiceId: current.id, totalDue: current.totals.total_due });
      toast.success(existingInvoiceId ? "Draft updated" : "Draft saved");
    } catch (err) {
      toast.error(errorMessage(err, "Could not render invoice PDF"));
    } finally {
      setWorking(false);
    }
  }, [savedInvoice, fetchPdfAsBase64, onReady, existingInvoiceId]);

  const handleIssue = useCallback(async () => {
    const current = await savedInvoice();
    if (!current) return;
    setWorking(true);
    try {
      const issued = await axios.post(`${API}/invoices/${current.id}/issue`, {}, {
        headers: { "X-Bonsai-User-Id": sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "" },
      });
      const pdfBase64 = await fetchPdfAsBase64(current.id);
      const filename = invoiceFileName(current.matter_ref, issued.data.invoice_number);
      onReady({
        pdfBase64, filename, invoiceNumber: issued.data.invoice_number,
        invoiceId: current.id, totalDue: issued.data.totals.total_due,
        creditControl: issued.data.credit_control || null,
      });
      toast.success(`Invoice #${issued.data.invoice_number} issued`);
    } catch (err) {
      toast.error(errorMessage(err, "Could not issue invoice"));
    } finally {
      setWorking(false);
    }
  }, [savedInvoice, fetchPdfAsBase64, onReady]);

  if (!started) {
    return (
      <Button size="sm" onClick={handleGenerate} disabled={loading} className="bg-teal hover:bg-teal-hover text-white gap-1.5" data-testid="native-invoice-generate-btn">
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        {loading ? "Generating…" : "Generate Invoice"}
      </Button>
    );
  }

  if (loading || !invoice) {
    return <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-3 p-3 rounded-md border border-teal/30 bg-teal-dim/10" data-testid="native-invoice-editor">
      {invoice.manual_other_fee_notes?.length > 0 && (
        <div className="p-2 rounded border border-amber-200 bg-status-amber-dim text-xs text-status-amber space-y-1" data-testid="native-invoice-manual-notes">
          <div className="flex items-center gap-1.5 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> Needs a manual line under Other Fees — not auto-priced:</div>
          {invoice.manual_other_fee_notes.map((n, i) => <div key={i}>• {n}</div>)}
        </div>
      )}

      <div data-testid="native-invoice-to">
        <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Invoice to</div>
        <input
          type="text"
          value={invoice.client_display_name || ""}
          onChange={(e) => handleInvoiceTo("client_display_name", e.target.value)}
          placeholder="Company or individual(s)"
          aria-label="Invoice to name"
          className="w-full p-1.5 rounded border border-white/10 text-xs mb-1"
          data-testid="native-invoice-to-name"
        />
        <textarea
          value={invoice.invoice_to_address || ""}
          onChange={(e) => handleInvoiceTo("invoice_to_address", e.target.value)}
          placeholder="Address (optional, one line per line)"
          aria-label="Invoice to address"
          rows={3}
          className="w-full p-1.5 rounded border border-white/10 text-xs"
          data-testid="native-invoice-to-address"
        />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="text-xs font-medium text-slate-400 uppercase tracking-wider">Professional Services</div>
          <button
            type="button"
            onClick={handleSortByDate}
            className="p-1 text-slate-400 hover:text-teal"
            title="Sort by date, oldest first"
            aria-label="Sort by date"
            data-testid="native-invoice-sort-by-date"
          >
            <CalendarArrowUp className="w-3.5 h-3.5" />
          </button>
        </div>
        {invoice.professional_services.map((l, i) => (
          <LineRow key={l.id || l._k || i} line={l} onChange={(next) => handleLineChange("professional_services", i, next)} onRemove={() => handleRemoveLine("professional_services", i)} showBilling showWriteOff drag={dragProps("professional_services", i)} />
        ))}
        <button type="button" onClick={() => handleAddLine("professional_services")} className="text-xs text-teal hover:underline flex items-center gap-1 mt-1">
          <Plus className="w-3 h-3" /> Add line
        </button>
        <div className="mt-2 flex items-center gap-2" data-testid="native-invoice-discount">
          <span className="text-xs text-slate-400 w-16 shrink-0">Discount</span>
          <button
            type="button"
            onClick={() => handleDiscount({ kind: invoice.discount?.kind === "percent" ? "amount" : "percent" })}
            className="p-1.5 rounded border border-white/10 text-slate-300 hover:text-teal"
            title={invoice.discount?.kind === "percent" ? "Percentage (click for £)" : "Amount in £ (click for %)"}
            aria-label="Discount in pounds or percent"
            data-testid="native-invoice-discount-kind"
          >
            {invoice.discount?.kind === "percent" ? <Percent className="w-3.5 h-3.5" /> : <PoundSterling className="w-3.5 h-3.5" />}
          </button>
          <input
            type="number"
            step="0.01"
            min="0"
            value={invoice.discount?.value ?? ""}
            onChange={(e) => handleDiscount({ value: e.target.value })}
            placeholder="0"
            aria-label="Discount value"
            className="w-20 p-1.5 rounded border border-white/10 text-xs text-right"
            data-testid="native-invoice-discount-value"
          />
          <input
            type="text"
            value={invoice.discount?.reason ?? ""}
            onChange={(e) => handleDiscount({ reason: e.target.value })}
            placeholder="Reason (printed on the invoice)"
            aria-label="Discount reason"
            className={`flex-1 p-1.5 rounded border text-xs ${Number(invoice.discount?.value) > 0 && !invoice.discount?.reason?.trim() ? "border-status-amber" : "border-white/10"}`}
            data-testid="native-invoice-discount-reason"
          />
        </div>
        {offeredTime.length > 0 && (
          <div className="mt-2 p-2 rounded border border-indigo-400/30 bg-indigo-500/10 text-xs text-slate-200 flex items-center gap-2" data-testid="native-invoice-new-time">
            <Clock className="w-3.5 h-3.5 text-indigo-300 shrink-0" />
            <Button size="sm" variant="outline" onClick={handleAddNewTime} className="h-6 text-xs gap-1 shrink-0" data-testid="native-invoice-add-new-time">
              <Plus className="w-3 h-3" /> Add
            </Button>
            <span className="flex-1">
              {offeredTime.length} time {offeredTime.length === 1 ? "entry" : "entries"} not on this invoice yet, £{offeredTotal.toFixed(2)}
            </span>
          </div>
        )}
      </div>

      <div>
        <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Disbursements &amp; Expenses</div>
        {invoice.disbursement_lines.map((l, i) => (
          <LineRow key={l.id || l._k || i} line={l} onChange={(next) => handleLineChange("disbursement_lines", i, next)} onRemove={() => handleRemoveLine("disbursement_lines", i)} showVat drag={dragProps("disbursement_lines", i)} />
        ))}
        <div className="flex items-center gap-3 mt-1">
          <button type="button" onClick={() => handleAddLine("disbursement_lines")} className="text-xs text-teal hover:underline flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add line
          </button>
          {invoice.matter_ref && (
            <a
              href={`/add-expense?matter=${encodeURIComponent(invoice.matter_ref)}`}
              className="text-xs text-slate-400 hover:text-teal flex items-center gap-1"
              title="The bin here takes a line off this invoice only. To delete an expense added in error, open the matter's expenses."
              data-testid="native-invoice-matter-expenses-link"
            >
              <Receipt className="w-3 h-3" /> Matter expenses
            </a>
          )}
        </div>
      </div>

      <div>
        <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Other Fees</div>
        {(invoice.other_fee_lines || []).map((l, i) => (
          <LineRow key={l.id || l._k || i} line={l} onChange={(next) => handleLineChange("other_fee_lines", i, next)} onRemove={() => handleRemoveLine("other_fee_lines", i)} showVat drag={dragProps("other_fee_lines", i)} />
        ))}
        <button type="button" onClick={() => handleAddLine("other_fee_lines")} className="text-xs text-teal hover:underline flex items-center gap-1 mt-1">
          <Plus className="w-3 h-3" /> Add line
        </button>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-white/10 text-sm">
        <span className="text-slate-400">Total due</span>
        <span className="font-semibold text-teal" data-testid="native-invoice-total-due">
          {fmtGbp(liveTotalDue(invoice) ?? invoice.totals.total_due)}
          {dirty && <span className="ml-1.5 text-[10px] font-normal text-slate-400" data-testid="native-invoice-unsaved">unsaved</span>}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={handleSave} disabled={saving || working} data-testid="native-invoice-save-btn">
          {saving ? "Saving…" : "Save changes"}
        </Button>
        {stage === "draft" ? (
          <Button size="sm" onClick={handleUseAsDraft} disabled={working || saving} className="bg-teal hover:bg-teal-hover text-white gap-1.5" data-testid="native-invoice-use-as-draft-btn">
            <FileCheck className="w-3.5 h-3.5" /> {working ? "Saving…" : existingInvoiceId ? "Update draft" : "Save draft"}
          </Button>
        ) : (
          <Button size="sm" onClick={handleIssue} disabled={working || saving} className="bg-teal hover:bg-teal-hover text-white gap-1.5" data-testid="native-invoice-issue-btn">
            <FileCheck className="w-3.5 h-3.5" /> {working ? "Finalising…" : "Finalise invoice"}
          </Button>
        )}
      </div>
    </div>
  );
}
